import React from "react";

export default function Privacy() {
  return (
    <>
      <div className="section-container reading-sections privacy">
        <section>
          <h1 className="section-header">Privacy Policy</h1>
          <p>Last updated December 09, 2021</p>
          <p>
            Thank you for using{" "}
            <span className="text-acc-clr-400"> Bejour - Behavioral Economics Journal </span> ("we",
            "us", "our"). We are committed to protecting your personal information and your right to
            privacy. If you have any questions or concerns about this privacy notice, or our
            practices with regards to your personal information, please contact us at
            info@dispassion.blog.
          </p>
          <p>
            When you visit our website https://bejour.web.app/ (the Website), and more generally,
            use any of our services (the Services, which include the Website), we appreciate that
            you are trusting us with your personal information. We take your privacy very seriously.
            In this privacy notice, we seek to explain to you in the clearest way possible what
            information we collect, how we use it and what rights you have in relation to it. We
            hope you take some time to read through it carefully, as it is important. If there are
            any terms in this privacy notice that you do not agree with, please discontinue use of
            our Services immediately.
          </p>
          <p>
            This privacy notice applies to all information collected through our Services (which, as
            described above, includes our Website), as well as any related services, sales,
            marketing or events.
          </p>
          <p>
            Please read this privacy notice carefully as it will help you understand what we do with
            the information that we collect.
          </p>
          <h2>TABLE OF CONTENTS</h2>
          <a className="link-text" href="#point-1">
            1. WHAT INFORMATION DO WE COLLECT?
          </a>
          <a className="link-text" href="#point-2">
            2. WILL YOUR INFORMATION BE SHARED WITH ANYONE?
          </a>
          <a className="link-text" href="#point-3">
            3. WHO WILL YOUR INFORMATION BE SHARED WITH?
          </a>
          <a className="link-text" href="#point-4">
            4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
          </a>
          <a className="link-text" href="#point-5">
            5. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
          </a>
          <a className="link-text" href="#point-6">
            6. HOW LONG DO WE KEEP YOUR INFORMATION?
          </a>
          <a className="link-text" href="#point-7">
            7. WHAT ARE YOUR PRIVACY RIGHTS?
          </a>
          <a className="link-text" href="#point-8">
            8. CONTROLS FOR DO-NOT-TRACK FEATURES
          </a>
          <a className="link-text" href="#point-9">
            9. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
          </a>
          <a className="link-text" href="#point-10">
            10. DO WE MAKE UPDATES TO THIS NOTICE?
          </a>
          <a className="link-text" href="#point-11">
            11. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
          </a>
          <a className="link-text" href="#point-12">
            12. HOW CAN YOU REVIEW, UPDATE OR DELETE THE DATA WE COLLECT FROM YOU?
          </a>
          <a className="link-text" href="#point-13">
            13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
          </a>
          <h2 id="point-1">1. WHAT INFORMATION DO WE COLLECT?</h2>
          <h3>Personal information you disclose to us</h3>
          <p>
            <em>In Short: We collect personal information that you provide to us.</em>
          </p>
          <p>
            We collect personal information that you voluntarily provide to us when you express an
            interest in obtaining information about us or our products and Services, when you
            participate in activities on the Website or otherwise when you contact us.
          </p>
          <p>
            The personal information that we collect depends on the context of your interactions
            with us and the Website, the choices you make and the products and features you use. The
            personal information we collect may include the following:
          </p>
          <h3>Information automatically collected (if consent is granted)</h3>
          <p>
            <em>
              In Short: Some information — such as your Internet Protocol (IP) address and/or
              browser and device characteristics — is collected automatically when you visit our
              Website.
            </em>
          </p>
          <p>
            We automatically collect certain information when you visit, use or navigate the
            Website. This information does not reveal your specific identity (like your name or
            contact information) but may include device and usage information, such as your IP
            address, browser and device characteristics, operating system, language preferences,
            referring URLs, device name, country, location, information about how and when you use
            our Website and other technical information. This information is primarily needed to
            maintain the security and operation of our Website, and for our analytics and reporting
            purposes (such as via Google Analytics). This information is only collected once consent
            is given.
          </p>
          <p>
            Like many websites, we also collect information through cookies and similar technologies
            (if consent is granted).
          </p>
          <p>The information we collect includes:</p>
          <p>
            <em>Log and Usage Data.</em> Log and usage data is service-related, diagnostic, usage
            and performance information our servers automatically collect when you access or use our
            Website. Depending on how you interact with us, this data may include your IP address,
            device information, browser type and settings and information about your activity in the
            Website (such as the date/time stamps associated with your usage, pages and files
            viewed, searches and other actions you take such as which features you use), device
            event information (such as system activity, error reports (sometimes called crash dumps)
            and hardware settings).
          </p>

          <h2 id="point-2">2. HOW DO WE USE YOUR INFORMATION?</h2>
          <p>
            <em>
              In Short: We process your information for purposes based on legitimate interests, the
              fulfillment of our contract with you, compliance with our legal obligations, and/or
              your consent.
            </em>
          </p>
          <p>
            We use personal information collected via our Website for a variety of purposes
            described below. We process your personal information for these purposes in reliance on
            our legitimate interests, and/or for compliance with our legal obligations.
          </p>
          <p>We use the information we collect or receive:</p>
          <ul>
            <li>To understand how visitors use the site</li>
            <li>To provide the Services offered on the site</li>
          </ul>
          <h2 id="point-3">3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?</h2>
          <p>
            <em>
              In Short: We only share information with your consent, to comply with laws, to provide
              you with services, to protect your rights, or to fulfill obligations.
            </em>
          </p>
          <p>We may process or share your data that we hold based on the following legal basis:</p>
          <ul>
            <li>
              Consent: We may process your data if you have given us specific consent to use your
              personal information for a specific purpose.
            </li>
            <li>
              Performance of a Contract: Where we have entered into a contract with you, we may
              process your personal information to fulfill the terms of our contract.
            </li>
            <li>
              Legal Obligations: We may disclose your information where we are legally required to
              do so in order to comply with applicable law, governmental requests, a judicial
              proceeding, court order, or legal process, such as in response to a court order or a
              subpoena (including in response to public authorities to meet national security or law
              enforcement requirements).
            </li>
            <li>
              Vital Interests: We may disclose your information where we believe it is necessary to
              investigate, prevent, or take action regarding potential violations of our policies,
              suspected fraud, situations involving potential threats to the safety of any person
              and illegal activities, or as evidence in litigation in which we are involved.
            </li>
          </ul>
          <p>
            More specifically, we may need to process your data or share your personal information
            in the following situations:
          </p>
          <ul>
            <li>
              Third-Party Service Providers. We may share your data with third-party service
              providers who perform services for us or on our behalf and require access to such
              information to do that work. We may allow selected third parties to use tracking
              technology on the Website, which will enable them to collect data on our behalf about
              how you interact with our Website over time. This information may be used to, among
              other things, analyze and track data, determine the popularity of certain content,
              pages or features, and better understand online activity. Unless described in this
              notice, we do not share, sell, rent or trade any of your information with third
              parties for their promotional purposes.
            </li>

            <li>
              Regarding embedded content from other websites: Articles on this site may include
              embedded content (e.g. videos, images, articles, etc.). Embedded content from other
              websites behaves in the exact same way as if the visitor has visited the other
              website. These websites may collect data about you, use cookies, embed additional
              third-party tracking, and monitor your interaction with that embedded content,
              including tracking your interaction with the embedded content if you have an account
              and are logged in to that website.
            </li>
          </ul>
          <h2 id="point-4">4. WHO WILL YOUR INFORMATION BE SHARED WITH?</h2>
          <p>
            <em>In Short: We only share information with the following third parties.</em>
          </p>
          <p>
            We only share and disclose your information with the following third parties. If we have
            processed your data based on your consent and you wish to revoke your consent, please
            contact us using the contact details provided in the section below titled "HOW CAN YOU
            CONTACT US ABOUT THIS NOTICE?".
          </p>
          <ul>
            <li>
              Web and Mobile Analytics (specifically Google Analytics). Read more about how Google
              uses your Personal Information here https://policies.google.com/privacy or opt-out of
              Google Analytics here https://tools.google.com/dlpage/gaoptout
            </li>
          </ul>
          <h2 id="point-5">5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
          <p>
            <em>
              In Short: We may use cookies and other tracking technologies to collect and store your
              information.
            </em>
          </p>
          <p>
            We may use cookies and similar tracking technologies to access or store information, see
            "WHAT INFORMATION DO WE COLLECT?" above.
          </p>
          <h2 id="point-6">6. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</h2>
          <p>
            <em>
              In Short: We may transfer, store, and process your information in countries other than
              your own.
            </em>
          </p>
          <p>
            Our servers are located in Germany. If you are accessing our Website from outside,
            please be aware that your information may be transferred to, stored, and processed by us
            in our facilities and by those third parties with whom we may share your personal
            information (see "WILL YOUR INFORMATION BE SHARED WITH ANYONE?" above) in other
            countries.
          </p>
          <p>
            If you are a resident in the European Economic Area (EEA) or United Kingdom (UK), then
            these countries may not necessarily have data protection laws or other similar laws as
            comprehensive as those in your country. We will however take all necessary measures to
            protect your personal information in accordance with this privacy notice and applicable
            law.
          </p>
          <h2 id="point-7">7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
          <p>
            <em>
              In Short: We keep your information for as long as necessary to fulfill the purposes
              outlined in this privacy notice unless otherwise required by law.
            </em>
          </p>
          <p>
            We will only keep your personal information for as long as it is necessary for the
            purposes set out in this privacy notice, unless a longer retention period is required or
            permitted by law (such as tax, accounting or other legal requirements).
          </p>
          <p>
            When we have no ongoing legitimate need to process your personal information, we will
            either delete or anonymize such information, or, if this is not possible (for example,
            because your personal information has been stored in backup archives), then we will
            securely store your personal information and isolate it from any further processing
            until deletion is possible.
          </p>
          <h2 id="point-8">8. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
          <p>
            <em>
              In Short: In some regions, such as the European Economic Area (EEA) and United Kingdom
              (UK), you have rights that allow you greater access to and control over your personal
              information.You may review, change, or terminate your account at any time.
            </em>
          </p>
          <p>
            In some regions (like the EEA and UK), you have certain rights under applicable data
            protection laws. These may include the right (i) to request access and obtain a copy of
            your personal information, (ii) to request rectification or erasure (iii) to restrict
            the processing of your personal information and (iv) if applicable, to data portability.
            In certain circumstances, you may also have the right to object to the processing of
            your personal information. To make such a request, please use the contact details
            provided below. We will consider and act upon any request in accordance with applicable
            data protection laws.
          </p>
          <p>
            If we are relying on your consent to process your personal information, you have the
            right to withdraw your consent at any time. Please note however that this will not
            affect the lawfulness of the processing before its withdrawal, nor will it affect the
            processing of your personal information conducted in reliance on lawful processing
            grounds other than consent.
          </p>
          <p>
            If you are a resident in the EEA or UK and you believe we are unlawfully processing your
            personal information, you also have the right to complain to your local data protection
            supervisory authority. You can find their contact details here:
            http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm.
          </p>
          <p>
            If you are a resident in Switzerland, the contact details for the data protection
            authorities are available here: https://www.edoeb.admin.ch/edoeb/en/home.html.
          </p>
          <p>
            <u>Cookies and similar technologies:</u> Most Web browsers are set to accept cookies by
            default. If you prefer, you can usually choose to set your browser to remove cookies and
            to reject cookies. If you choose to remove cookies or reject cookies, this could affect
            certain features or services of our Website.
          </p>
          <h2 id="point-9">9. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
          <p>
            Most web browsers and some mobile operating systems and mobile applications include a
            Do-Not-Track (DNT) feature or setting you can activate to signal your privacy preference
            not to have data about your online browsing activities monitored and collected. At this
            stage no uniform technology standard for recognizing and implementing DNT signals has
            been finalized. As such, we do not currently respond to DNT browser signals or any other
            mechanism that automatically communicates your choice not to be tracked online. If a
            standard for online tracking is adopted that we must follow in the future, we will
            inform you about that practice in a revised version of this privacy notice.{" "}
          </p>
          <h2 id="point-10">10. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
          <p>
            <em>
              In Short: Yes, if you are a resident of California, you are granted specific rights
              regarding access to your personal information.
            </em>
          </p>
          <p>
            California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits
            our users who are California residents to request and obtain from us, once a year and
            free of charge, information about categories of personal information (if any) we
            disclosed to third parties for direct marketing purposes and the names and addresses of
            all third parties with which we shared personal information in the immediately preceding
            calendar year. If you are a California resident and would like to make such a request,
            please submit your request in writing to us using the contact information provided
            below.
          </p>
          <p>
            If you are under 18 years of age, reside in California, and have a registered account
            with the Website, you have the right to request removal of unwanted data that you
            publicly post on the Website. To request removal of such data, please contact us using
            the contact information provided below, and include the email address associated with
            your account and a statement that you reside in California. We will make sure the data
            is not publicly displayed on the Website, but please be aware that the data may not be
            completely or comprehensively removed from all our systems (e.g. backups, etc.).
          </p>
          <h2 id="point-11">11. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
          <p>
            <em>
              In Short: Yes, we will update this notice as necessary to stay compliant with relevant
              laws.
            </em>
          </p>
          <p>
            We may update this privacy notice from time to time. The updated version will be
            indicated by an updated "Revised" date and the updated version will be effective as soon
            as it is accessible. If we make material changes to this privacy notice, we may notify
            you either by prominently posting a notice of such changes or by directly sending you a
            notification. We encourage you to review this privacy notice frequently to be informed
            of how we are protecting your information.
          </p>
          <h2 id="point-12">12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
          <p>
            If you have questions or comments about this notice, you may email us at
            info@dispassion.blog.
          </p>
          <h2 id="point-13">
            13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
          </h2>
          <p>
            Based on the applicable laws of your country, you may have the right to request access
            to the personal information we collect from you, change that information, or delete it
            in some circumstances. To request to review, update, or delete your personal
            information, please email us at info@dispassion.blog.
          </p>
        </section>
      </div>
    </>
  );
}
